import { useCallback, useMemo } from 'react';
import { MenuItem, SelectField, ExpandableFilter, Box, useMediaQuery } from 'bonfire-kindling';
import { useTranslation } from 'react-i18next';

import {
  SORT_BY,
  PROJECT_STATUS,
  PROJECT_SOURCE,
  PROJECT_VIEW_STATUS,
} from 'constants/opportunities';
import { useFeatureFlags } from 'helpers/hooks';
import FilterByLocation from './FilterByLocation';
import OrganizationDropdown from './OrganizationDropdown';
import FilterByOpportunityType from './FilterByOpportunityType';
import OpportunitiesSavedSearch from './OpportunitiesSavedSearch';

export default function OpportunitiesSearchBelt({
  onSortChange,
  onSearchChange,
  onFilterChange,
  availableTypes,
  availableLocations,
  effectiveLocations,
  currentSearchFilter,
}) {
  const { t } = useTranslation();
  const isMobileWidth = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const { isFlagEnabled } = useFeatureFlags();

  const onLocationsChange = useCallback(
    locations => onFilterChange({ locations }),
    [onFilterChange]
  );

  const onStatusesChange = useCallback(statuses => onFilterChange({ statuses }), [onFilterChange]);

  const onViewStatusChange = useCallback(
    viewStatus => onFilterChange({ viewStatus }),
    [onFilterChange]
  );

  const onTypesChange = useCallback(types => onFilterChange({ types }), [onFilterChange]);

  const onSourcesChange = useCallback(sources => onFilterChange({ sources }), [onFilterChange]);

  const onOrganizationsChange = useCallback(
    organizations => onFilterChange({ organizations }),
    [onFilterChange]
  );

  const onReset = useCallback(() => {
    onFilterChange({
      statuses: PROJECT_STATUS.open,
      sources: PROJECT_SOURCE.allSources,
      locations: null,
      organizations: [],
      types: availableTypes,
      viewStatus: PROJECT_VIEW_STATUS.allStatuses,
    });
  }, [onFilterChange, availableTypes]);

  const { search, statuses, types, organizations, sources, viewStatus, sortBy } =
    currentSearchFilter;

  const filterValues = useMemo(
    () => ({
      status: {
        initialValues: PROJECT_STATUS.open,
        currentValues: statuses,
      },
      type: {
        initialValues: availableTypes,
        currentValues: types,
      },
      location: {
        initialValues: availableLocations,
        currentValues: effectiveLocations,
      },
      organizations: {
        initialValues: [],
        currentValues: organizations,
      },
      source: {
        initialValues: PROJECT_SOURCE.allSources,
        currentValues: sources,
      },
      viewStatus: {
        initialValues: PROJECT_VIEW_STATUS.allStatuses,
        currentValues: viewStatus,
      },
    }),
    [
      availableLocations,
      availableTypes,
      effectiveLocations,
      organizations,
      sources,
      statuses,
      types,
      viewStatus,
    ]
  );
  // Avoids fragment use with a ternary expression since that bugs out `SelectField`.
  const sortByMenuItemProps =
    statuses === PROJECT_STATUS.closed
      ? [{ label: t('opportunities.filters.closing_soon'), value: SORT_BY.closingSoon }]
      : [
          { label: t('opportunities.filters.most_recent'), value: SORT_BY.mostRecent },
          { label: t('opportunities.filters.closing_soon'), value: SORT_BY.closingSoon },
        ];

  return (
    <ExpandableFilter
      variant={isMobileWidth ? 'drawer' : 'card'}
      filterLabel={t('opportunities.filters.filter_by')}
      filterLabelProps={{
        variant: 'body2',
      }}
      showLabel={t('opportunities.filters.show_filters')}
      hideLabel={t('opportunities.filters.hide_filters')}
      resetLabel={t('opportunities.filters.reset_filters')}
      searchProps={{
        name: 'search',
        onChange: e => onSearchChange(e.target.value),
        value: search,
        placeholder: t('opportunities.filters.search'),
        type: 'search',
        sx: { width: { xs: '15rem', sm: '10rem', md: '20rem' } },
      }}
      filterValues={filterValues}
      onReset={onReset}
      filterControlsRightContent={
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            flex: '1 1 0%',
          }}
        >
          <OpportunitiesSavedSearch />
          <Box sx={{ ml: 'auto' }}>
            <SelectField
              label={t('opportunities.filters.sort_by')}
              name="sort"
              value={sortBy}
              onChange={onSortChange}
              inlineLabel
            >
              {sortByMenuItemProps.map(({ label, value }) => (
                <MenuItem key={value} label={label} value={value} />
              ))}
            </SelectField>
          </Box>
        </Box>
      }
      SwipeableDrawerProps={{
        PaperProps: {
          sx: {
            top: theme => theme.mixins.toolbar.minHeight,
            height: theme => `calc(100% - ${theme.mixins.toolbar.minHeight}px)`,
          },
        },
      }}
    >
      <SelectField
        label={t('opportunities.filters.status')}
        name="statuses"
        value={statuses}
        onChange={e => onStatusesChange(e.target.value)}
        SelectDisplayProps={{
          'data-cy': 'opportunities-filter-statuses',
          'data-testid': 'opportunities-filter-statuses',
        }}
      >
        <MenuItem
          value={PROJECT_STATUS.allOpportunities}
          label={t('opportunities.filters.status_all')}
        />
        <MenuItem value={PROJECT_STATUS.open} label={t('opportunities.filters.status_open')} />
        <MenuItem value={PROJECT_STATUS.closed} label={t('opportunities.filters.status_closed')} />
      </SelectField>
      <FilterByOpportunityType
        onChange={onTypesChange}
        values={types}
        availableValues={availableTypes}
      />
      <OrganizationDropdown value={organizations} onChange={onOrganizationsChange} />
      <FilterByLocation
        onChange={onLocationsChange}
        values={effectiveLocations}
        availableValues={availableLocations}
        label={t('opportunities.filters.location')}
      />
      <SelectField
        label={t('opportunities.filters.opportunity_source')}
        name="sources"
        value={sources}
        onChange={e => onSourcesChange(e.target.value)}
      >
        <MenuItem
          value={PROJECT_SOURCE.allSources}
          label={t('opportunities.filters.opportunity_source_all')}
        />
        <MenuItem
          value={PROJECT_SOURCE.bonfire}
          label={t('opportunities.filters.opportunity_source_bonfire')}
        />
        <MenuItem
          value={PROJECT_SOURCE.external}
          label={t('opportunities.filters.opportunity_source_external')}
        />
      </SelectField>
      {isFlagEnabled('FF_18_RELEASE_TOGGLE_PV_VIEWED_PROJECTS') && (
        <SelectField
          label={t('opportunities.filters.view_status')}
          name="viewStatus"
          value={viewStatus}
          onChange={e => onViewStatusChange(e.target.value)}
        >
          <MenuItem
            value={PROJECT_VIEW_STATUS.allStatuses}
            label={t('opportunities.filters.view_status_all')}
          />
          <MenuItem
            value={PROJECT_VIEW_STATUS.viewed}
            label={t('opportunities.filters.view_status_viewed')}
          />
          <MenuItem
            value={PROJECT_VIEW_STATUS.notViewed}
            label={t('opportunities.filters.view_status_not_viewed')}
          />
        </SelectField>
      )}
    </ExpandableFilter>
  );
}
